/**
 * InfinitSales - Chat Utilities (Simplified)
 * Simple formatting utilities for chat display
 * Business logic (deduplication, normalization) is handled by backend
 * 
 * UPDATED v2:
 * - Fixed: "Você" appearing as chat name when lastMessage is fromMe
 */

import { isBrNationalNumber } from '../../components/contacts/createPhoneRule';

/**
 * Check if JID is a group
 */
export function isGroup(jid) {
  return jid?.includes('@g.us') || false;
}

/**
 * Check if a JID/identifier is a LID (identidade oculta do WhatsApp).
 * LIDs chegam como "123...@lid" ou mascarados com 14+ dígitos — nunca são
 * telefone real (E164 tem no máximo 13 dígitos) e não devem ser exibidos
 * como número.
 */
export function isLidIdentifier(value) {
  if (!value) return false;
  const str = String(value);
  if (str.includes('@lid')) return true;
  const digits = str.split('@')[0].replace(/\D/g, '');
  return digits.length >= 14;
}

/**
 * Extract a REAL phone (10-13 dígitos) from a JID.
 * Returns null for groups, LIDs and anything that isn't a phone.
 * (Era chamado em vários pontos mas não existia — TypeError latente.)
 */
export function extractPhone(jid) {
  if (!jid || isGroup(jid) || isLidIdentifier(jid)) return null;
  const digits = String(jid).split('@')[0].replace(/\D/g, '');
  return digits.length >= 10 && digits.length <= 13 ? digits : null;
}

/**
 * Get display name for a chat
 * Priority: pushName > name > lastMessage.pushName (only if NOT fromMe) > formatted phone
 * 
 * Note: Evolution API often returns chats without pushName,
 * but the pushName comes in the messages via webhook/SSE.
 * So we also check lastMessage.pushName as fallback.
 * 
 * IMPORTANT: We should NOT use lastMessage.pushName when the message is fromMe,
 * because it would show "Você" as the chat name.
 */
// Um "nome" que é só dígitos (10+) é número vazando como pushName — docs
// antigos criados antes do fix do backend. Não tratar como nome real.
export function isUsableName(name) {
  if (!name) return false;
  const str = String(name).trim();
  if (!str || str === 'Você') return false;
  if (/^\+?[\d\s()-]+$/.test(str) && str.replace(/\D/g, '').length >= 10) return false;
  return true;
}

// Identidade ainda e so um LID (sem telefone real): contato em
// sincronizacao. O reconcile LID->telefone troca pelo nome real e a lista
// atualiza ao vivo via CHAT_UPDATE (SSE). Mostrado no lugar do numero cru.
export const SYNCING_CONTACT_LABEL = 'Sincronizando contato…';

export function getChatDisplayName(chat) {
  // Direct chat properties
  if (isUsableName(chat.pushName)) return chat.pushName;
  if (isUsableName(chat.name)) return chat.name;

  // Try to get from lastMessage (comes from SSE/webhook)
  // BUT only if it's NOT from me (to avoid showing "Você" as name)
  // and NOT a group (pushName seria o participante, não o grupo)
  const lastMsg = chat.lastMessage;
  const isFromMe = lastMsg?.key?.fromMe === true;
  const lastMsgPushName = lastMsg?.pushName;

  if (isUsableName(lastMsgPushName) && !isFromMe && !isGroup(chat.remoteJid)) {
    return lastMsgPushName;
  }

  // Grupo sem nome conhecido: o id do grupo nao e telefone — nunca formatar.
  if (isGroup(chat.remoteJid)) return 'Grupo';

  const phone = chat._phoneNumber || chat.remoteJid?.split('@')[0];
  if (!phone) return 'Desconhecido';
  // LID puro (sem telefone real ainda): contato em sincronizacao.
  if (isLidIdentifier(phone) || isLidIdentifier(chat.remoteJid)) {
    return SYNCING_CONTACT_LABEL;
  }
  return formatPhoneNumber(phone) || 'Desconhecido';
}

/**
 * Format phone number for display
 */
export function formatPhoneNumber(phone) {
  if (!phone) return '';

  const clean = String(phone).replace(/\D/g, '');

  // LID (14+ dígitos) NUNCA é telefone — não há número real para exibir.
  // Devolve vazio para o campo de número ficar oculto; o NOME carrega o
  // rótulo "Sincronizando contato…" (ver getChatDisplayName).
  if (clean.length >= 14 || String(phone).includes('@lid')) {
    return '';
  }

  // Brazilian format: +55 (11) 99999-9999
  if (clean.length === 13 && clean.startsWith('55')) {
    const ddd = clean.slice(2, 4);
    const part1 = clean.slice(4, 9);
    const part2 = clean.slice(9);
    return `+55 (${ddd}) ${part1}-${part2}`;
  }

  if (clean.length === 12 && clean.startsWith('55')) {
    const ddd = clean.slice(2, 4);
    const part1 = clean.slice(4, 8);
    const part2 = clean.slice(8);
    return `+55 (${ddd}) ${part1}-${part2}`;
  }

  // BR sem DDI, mas SO quando os digitos tem a cara de numero BR — jids do
  // WhatsApp sempre incluem DDI, entao 10-11 digitos aqui pode ser numero
  // ESTRANGEIRO completo (EUA com DDI 1 tem 11): celular BR e DDD(2)+9+8
  // (terceiro digito 9), fixo BR e DDD(2)+[2-5]+7. O DDD precisa existir no
  // Brasil (isBrNationalNumber): Chile 56912345678 tem a mesma forma, mas 56
  // nao e DDD, entao cai no internacional (auditoria de 25/09/2026).
  if (clean.length === 11 && /^[1-9]\d9\d{8}$/.test(clean) && isBrNationalNumber(clean)) {
    return `(${clean.slice(0, 2)}) ${clean.slice(2, 7)}-${clean.slice(7)}`;
  }
  if (clean.length === 10 && /^[1-9]\d[2-5]\d{7}$/.test(clean) && isBrNationalNumber(clean)) {
    return `(${clean.slice(0, 2)}) ${clean.slice(2, 6)}-${clean.slice(6)}`;
  }

  // Internacional (nao-BR): NAO assumir codigo de pais de 2 digitos — um US
  // '14155552671' virava '+14 155552671' (tratava o "1" como "14"). Sem
  // libphonenumber no cliente, exibe E.164 limpo com '+' e sem agrupar errado.
  if (clean.length > 10) {
    return `+${clean}`;
  }

  return phone;
}

/**
 * Get text preview from lastMessage
 */
export function getMessagePreview(chat) {
  const msg = chat.lastMessage?.message;
  if (!msg || typeof msg !== 'object') return '';

  // Sem emojis de tipo: o ChatList ja renderiza um icone lucide ao lado do
  // preview (getMessageIcon), entao o emoji virava redundancia ("[icone] Foto").
  if (msg.conversation) return msg.conversation;
  if (msg.extendedTextMessage?.text) return msg.extendedTextMessage.text;
  if (msg.imageMessage) return msg.imageMessage.caption || 'Foto';
  if (msg.videoMessage) return msg.videoMessage.caption || 'Vídeo';
  if (msg.audioMessage) return 'Mensagem de voz';
  if (msg.documentMessage) return msg.documentMessage.fileName || 'Documento';
  if (msg.stickerMessage) return 'Figurinha';
  if (msg.reactionMessage) return `Reagiu: ${msg.reactionMessage.text || '👍'}`;
  if (msg.contactMessage) return 'Contato';
  if (msg.locationMessage) return 'Localização';
  if (msg.pollCreationMessage) return 'Enquete';

  return '';
}

/**
 * Format timestamp for chat list
 */
export function formatChatTime(timestamp) {
  if (!timestamp) return '';
  
  const date = new Date(timestamp);
  const now = new Date();
  const diff = now - date;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (days === 0) {
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  } else if (days === 1) {
    return 'Ontem';
  } else if (days < 7) {
    return date.toLocaleDateString('pt-BR', { weekday: 'short' });
  } else {
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  }
}

/**
 * Check if two chats represent the same contact
 * Uses _phoneNumber from backend normalization
 */
export function isSameChat(chat1, chat2) {
  if (!chat1 || !chat2) return false;
  
  // Direct JID match
  if (chat1.remoteJid === chat2.remoteJid) return true;
  
  // Phone number match (from backend)
  if (chat1._phoneNumber && chat2._phoneNumber) {
    return chat1._phoneNumber === chat2._phoneNumber;
  }
  
  // Alt JID match
  if (chat1._altJid && (chat1._altJid === chat2.remoteJid || chat1._altJid === chat2._altJid)) {
    return true;
  }
  if (chat2._altJid && chat2._altJid === chat1.remoteJid) {
    return true;
  }
  
  return false;
}

/**
 * Find chat index in array by remoteJid or phone.
 * rawJid: forma CRUA do jid vinda do webhook (ex.: "...@lid") — o backend
 * envia o canônico em remoteJid e o cru em rawRemoteJid; casar pelos dois
 * evita criar linha duplicada quando a lista ainda tem o chat @lid legado.
 */
export function findChatIndex(chats, remoteJid, altJid = null, rawJid = null) {
  // Só trata como telefone o que É telefone (10-13 dígitos) — dígitos de LID
  // contaminavam o match por _phoneNumber.
  const targetPhone = extractPhone(remoteJid);

  return chats.findIndex(chat => {
    // Direct match
    if (chat.remoteJid === remoteJid) return true;
    if (altJid && chat.remoteJid === altJid) return true;
    if (rawJid && chat.remoteJid === rawJid) return true;

    // Alt JID match
    if (chat._altJid === remoteJid) return true;
    if (altJid && chat._altJid === altJid) return true;
    if (rawJid && chat._altJid === rawJid) return true;

    // Phone match
    if (targetPhone && chat._phoneNumber === targetPhone) return true;

    return false;
  });
}

/**
 * Get JID for sending messages (prefer @s.whatsapp.net)
 */
export function getMessageJid(chat) {
  if (chat.remoteJid?.includes('@s.whatsapp.net')) {
    return chat.remoteJid;
  }
  
  if (chat._altJid?.includes('@s.whatsapp.net')) {
    return chat._altJid;
  }
  
  if (chat._phoneNumber && /^\d+$/.test(chat._phoneNumber)) {
    return `${chat._phoneNumber}@s.whatsapp.net`;
  }
  
  return chat.remoteJid;
}

export default {
  SYNCING_CONTACT_LABEL,
  isGroup,
  isLidIdentifier,
  extractPhone,
  isUsableName,
  getChatDisplayName,
  formatPhoneNumber,
  getMessagePreview,
  formatChatTime,
  isSameChat,
  findChatIndex,
  getMessageJid,
};